import type { ParticipationCapabilities } from './ParticipationLayer';
import type { PresentationCard } from './PresentationCard';
import type { StageFeedbackTarget } from './StageFeedback';
import type { TopicAgendaItem } from './TalkShowTopics';
import type { MusicNowPlaying } from './MusicNowPlaying';

export const TOWNHALL_AUDIENCE_ACTIONS = [
    'vote',
    'poll_response',
    'reaction',
    'raise_hand',
    'question',
    'chat',
    'sentiment_vote',
] as const;

export type TownhallAudienceAction =
    typeof TOWNHALL_AUDIENCE_ACTIONS[number];

export const TOWNHALL_AUDIENCE_EVENT_SOURCES = [
    'companion_app',
    'youtube_chat',
    'tiktok_chat',
    'x_chat',
] as const;

export type TownhallAudienceEventSource =
    typeof TOWNHALL_AUDIENCE_EVENT_SOURCES[number];

export const TOWNHALL_AUDIENCE_EVENT_TYPES = [
    'transcript',
    'poll_update',
    'vote_result',
    'room_state',
    'reaction_wave',
    'promotion_status',
    'audience_count',
    'chat_message',
    'stage_overlay',
    'heartbeat',
    'sentiment_update',
    'topic_update',
    'music_now_playing',
] as const;

export type AudienceEventType = typeof TOWNHALL_AUDIENCE_EVENT_TYPES[number];

export const TOWNHALL_PROMOTION_QUEUE_STATUSES = [
    'requested',
    'approved',
    'auth_pending',
    'joined',
    'expired',
    'declined',
    'demoted',
] as const;

export type PromotionQueueStatus =
    typeof TOWNHALL_PROMOTION_QUEUE_STATUSES[number];

export const TOWNHALL_ROOM_STATUSES = [
    'scheduled',
    'live',
    'paused',
    'ended',
] as const;

export type TownhallRoomStatus = typeof TOWNHALL_ROOM_STATUSES[number];

export const TOWNHALL_POLL_TYPES = ['poll', 'vote'] as const;

export type TownhallPollType = typeof TOWNHALL_POLL_TYPES[number];

export const TOWNHALL_POLL_STATUSES = [
    'draft',
    'open',
    'closed',
    'archived',
] as const;

export type TownhallPollStatus = typeof TOWNHALL_POLL_STATUSES[number];

export const CHAT_INGESTION_RULE_TRIGGERS = [
    'keyword',
    'prefix',
    'regex',
    'emoji',
] as const;

export type ChatIngestionRuleTrigger =
    typeof CHAT_INGESTION_RULE_TRIGGERS[number];

export interface AudienceIdentity {
    source: TownhallAudienceEventSource;
    key: string;
    displayName?: string;
}

/**
 * Where an audience session entered from, when it came from X.
 * `x_inline` = X's in-app link view; `x_card` = an X Player Card iframe;
 * `x_link` = an ordinary browser tab opened from an X post (desktop x.com, or
 * Open on Telegames from the card), recognised by the `src=x…` link tag.
 * See `_docs/Q4/XInlineRooms/X-Inline-Rooms-FunctionalSpec.md` (item 19).
 */
export const AUDIENCE_ENTRY_SURFACES = ['x_inline', 'x_card', 'x_link'] as const;

export type AudienceEntrySurface = typeof AUDIENCE_ENTRY_SURFACES[number];

export const isAudienceEntrySurface = (value: unknown): value is AudienceEntrySurface =>
    typeof value === 'string' && (AUDIENCE_ENTRY_SURFACES as readonly string[]).includes(value);

/** Arrivals from X get no stage path in v1: interactivity without voice. */
export const isVoicelessEntrySurface = (surface: AudienceEntrySurface | undefined): boolean =>
    surface === 'x_inline' || surface === 'x_card' || surface === 'x_link';

export interface AudienceSessionToken {
    sid: string;
    roomId: string;
    layer: 'audience_gallery';
    capabilities: ParticipationCapabilities;
    displayName?: string;
    entrySurface?: AudienceEntrySurface;
    iat: number;
    exp: number;
}

export interface PromotionTokenClaims {
    promotionId: string;
    audienceSessionId: string;
    supabaseUserId: string;
    roomId: string;
    layer: 'guest_temp';
    capabilities: ParticipationCapabilities;
    iat: number;
    exp: number;
}

export interface TownhallPollOptionSnapshot {
    id: string;
    optionKey: string;
    label: string;
    count: number;
    percentage?: number;
    countBySource?: Partial<Record<TownhallAudienceEventSource, number>>;
}

export interface TownhallPollSnapshot {
    id: string;
    type: TownhallPollType;
    question: string;
    status: TownhallPollStatus;
    options: TownhallPollOptionSnapshot[];
    totalResponses: number;
    showResultsWhileOpen: boolean;
    allowChangeVote: boolean;
    maxSelectionsPerUser: number;
    openedAt?: number;
    closesAt?: number;
    createdBy?: string;
}

export interface TranscriptSegment {
    id: string;
    speakerLabel: string;
    text: string;
    timestamp: number;
    speakerId?: string;
    isFinal?: boolean;
}

export interface TownhallRoomStateSnapshot {
    status: TownhallRoomStatus;
    audienceCount?: number;
    activePollId?: string;
    guestParticipantId?: string;
    startedAt?: number;
    endedAt?: number;
}

export interface TownhallReactionWave {
    reaction: string;
    count: number;
    sampledAt: number;
    windowMs: number;
    /** Targeted stage/media coordinate for pluggable stage feedback effects. */
    target?: StageFeedbackTarget;
    /** Server-scheduled wall-clock start time for synchronized targeted feedback. */
    effectStartsAt?: number;
    /** Client-generated id used by senders to avoid replaying their own optimistic effect. */
    clientEventId?: string;
    sourceBreakdown?: Partial<Record<TownhallAudienceEventSource, number>>;
}

export interface PromotionQueueEntry {
    id: string;
    roomId: string;
    audienceSessionId: string;
    status: PromotionQueueStatus;
    requestedAt: number;
    approvedAt?: number;
    approvedBy?: string;
    supabaseUserId?: string;
    displayName?: string;
    promotionToken?: string;
    promotionTokenExpiresAt?: number;
    joinedAt?: number;
    joinedSessionId?: string;
    demotedAt?: number;
    reason?: string;
}

export interface TownhallChatMessage {
    id: string;
    source: TownhallAudienceEventSource;
    identityKey: string;
    text: string;
    timestamp: number;
    displayName?: string;
    matchedAction?: TownhallAudienceAction;
}

export interface BroadcastBriefSource {
    title: string;
    url: string;
    publisher?: string;
    publishedAt?: string;
}

export interface BroadcastBriefFact {
    text: string;
    sourceTitle?: string;
}

export interface BroadcastBriefStat {
    value: string;
    label: string;
    change?: string;
}

export type BroadcastBriefLayout =
    | 'executive_summary'
    | 'stat_forward'
    | 'timeline'
    | 'comparison'
    | 'market_watch'
    | 'breaking_context';

export interface BroadcastBrief {
    id: string;
    topic: string;
    generatedAt: number;
    layout: BroadcastBriefLayout;
    headline: string;
    deck: string;
    facts: BroadcastBriefFact[];
    keyStat?: BroadcastBriefStat;
    sourceChips: string[];
    sources: BroadcastBriefSource[];
    confidence: number;
    uncertainty?: string;
    imagePrompt?: string;
    visualAssetUrl?: string;
    visualAssetSource?: 'openai_image' | 'none';
}

export const TOWNHALL_OVERLAY_MODES = [
    'none',
    'poll',
    'poll_results',
    'vote',
    'question',
    'search',
    'video',
    'now_speaking',
    'caller_contribution',
    'sentiment',
    'presentation',
    'broadcast_brief',
    'web_stage',
    'web_stage_preview',
    'player_card',
    'scoreboard',
    'market_odds',
] as const;

export type TownhallOverlayMode = typeof TOWNHALL_OVERLAY_MODES[number];

/**
 * #3133 — a college-football card, as the room receives it.
 *
 * Native data on the existing overlay bus, exactly like a poll snapshot. NOT a
 * URL: `web_stage` exists because foreign content can only be rendered in an
 * iframe, and this is our own model — serialising it into query params so a page
 * could parse them back out would be a round trip to redraw something we are
 * already holding. (The compositor route does exactly that, and should: a
 * headless browser needs a URL to point at. A room does not.)
 *
 * Structurally mirrors what the card director publishes. Exactly one of `card`
 * or `comparison` is populated, and WHICH ONE decides whether one player or two
 * are drawn — the data decides, never a separate flag that could disagree with
 * what is on screen.
 */
export interface TownhallPlayerCardRow {
    label: string;
    value: string;
}

/**
 * #3133 §5.2 — face ids are PREFIXED when the subject is not a person.
 *
 * A card face carries a school, a schedule or a poll as readily as a player —
 * that was the intent of the shared label/value row from the start. The room's
 * renderer draws a headshot from the face id, and asking the headshot proxy for
 * `team:USC` would return a plate with a person's initials on a card about a
 * school.
 *
 * The prefix lives HERE, with the payload, because it is a contract between the
 * server that writes the id and the client that reads it — not a detail of
 * either side. `isTeamFace` is the one place that knows the rule.
 */
export const TEAM_FACE_PREFIX = 'team:';
export const SCHEDULE_FACE_PREFIX = 'schedule:';
export const RANKINGS_FACE_PREFIX = 'rankings:';
/** #3253 — a national position race: a field of people, not one person. */
export const RACE_FACE_PREFIX = 'race:';
/**
 * #3482 — a race scoped to ONE school, which the national one deliberately is
 * not. Distinct prefixes because the two are PAINTED differently: a national
 * race spans twenty-five schools and carries no team colour, a team race
 * carries its subject's.
 */
export const TEAM_RACE_FACE_PREFIX = 'teamrace:';

/** Is this face something other than one person — a school, poll or field? */
export function isTeamFace(playerId: string): boolean {
    return (
        playerId.startsWith(TEAM_FACE_PREFIX) ||
        playerId.startsWith(SCHEDULE_FACE_PREFIX) ||
        playerId.startsWith(RANKINGS_FACE_PREFIX) ||
        // A race is a field of people, so it has no single headshot either —
        // which is what this predicate is actually asked.
        playerId.startsWith(RACE_FACE_PREFIX) ||
        playerId.startsWith(TEAM_RACE_FACE_PREFIX)
    );
}

/**
 * One cell of the rankings grid — #3244.
 *
 * A poll is 25 teams, and forcing it into label/value rows rendered it as a row
 * of words. The mark carries the identity; the short name is the fallback when
 * no logo resolves, and the full `school` is what alt text says.
 */
export interface TownhallRankingsGridEntry {
    rank: number;
    school: string;
    shortName: string;
    teamId?: number;
    /** The team's own colour, which is what makes 25 cells scannable. */
    color?: string;
}

/**
 * One cell of a position-race grid — #3253.
 *
 * A person, themed on the school they play for: the mark and the colour belong
 * to the team, and the name under them does not. Its own type rather than a
 * reuse of the rankings cell, because a poll cell IS a school and this one only
 * borrows a school's colours — merging them would need a discriminator the
 * renderer could read wrong.
 */
export interface TownhallRaceGridEntry {
    rank: number;
    /** Full name, for alt text. */
    name: string;
    /** Surname, sized for the cell. */
    shortName: string;
    /** ESPN athlete id — CFBD's athlete id IS ESPN's, so a headshot needs no mapping. */
    playerId: string;
    team: string;
    /** School sized for a cell. */
    teamShort: string;
    /** The school's own abbreviation — "JMU", "SDSU". Preferred on a tile. */
    teamAbbr?: string;
    teamId?: number;
    color?: string;
    /** Already formatted — a cell has no room to decide. */
    value: string;
}

/**
 * One cell of the schedule grid — #3244.
 *
 * `home` is data rather than a label. The row form wrote "at Rutgers", which a
 * tile has no room for — and a schedule that does not say where a game is
 * played is wrong rather than terse, so the fact survives as something the
 * renderer marks however it likes.
 */
/** #3358 — one side of a Saturday-slate fixture. */
export interface TownhallSlateSide {
    school: string;
    shortName: string;
    teamId?: number;
    color?: string;
    /** Their poll rank, when they have one. Both sides may. */
    rank?: number;
    /** #4375 — this side's points once there is a score, drawn under its own mark. */
    points?: number;
}

/**
 * #3358 — one cell of a Saturday slate: a GAME, with both marks.
 *
 * Distinct from a schedule cell, which has one mark and a fixed subject. A
 * fixture cell has two, and sharing the schedule shape is what made the
 * opponent invisible.
 */
export interface TownhallSlateGridEntry {
    /** The best rank in the game — what the slate is ordered by. */
    rank: number;
    home: TownhallSlateSide;
    away: TownhallSlateSide;
    /** "3:00 PM ET". Absent once there is a score — they share a slot. */
    kickoff?: string;
    /** "3-42", AWAY first — the order the labels and the tile name the teams (#4375). */
    score?: string;
    /** "FINAL" or "LIVE". */
    status?: string;
}

export interface TownhallScheduleGridEntry {
    opponent: string;
    shortName: string;
    teamId?: number;
    color?: string;
    date: string;
    home: boolean;
    /** "W 34-17" once played. */
    result?: string;
}

/**
 * #3260 — the people on a team card.
 *
 * `label` is a role or a stat category, never a claim that someone starts:
 * CFBD has no depth chart, and a card captioned STARTERS that is really a
 * roster listing is the failure this avoids. These are PRODUCTION leaders,
 * which is right when a starter is hurt and cannot go stale.
 */
export interface TownhallTeamLeader {
    label: string;
    name: string;
    detail: string;
    /** ESPN athlete id, for the face. CFBD's athlete id IS ESPN's. */
    playerId?: string;
    /**
     * The player's LISTED position, which is not the slot.
     *
     * `label` says what he leads; this says what he is. On defence they never
     * agree — a TACKLES leader is a linebacker or a safety and the slot cannot
     * tell you which.
     */
    position?: string;
}

export interface TownhallTeamPeople {
    coach?: { name: string; record?: string; since?: number };
    leaders: TownhallTeamLeader[];
    /**
     * The timeframe the leaders' numbers cover — "THROUGH WEEK 1", "2025 SEASON".
     * The card's own `period` labels the rows, which can be a different season.
     */
    period?: string;
}

export interface TownhallPlayerCardFace {
    playerId: string;
    name: string;
    team: string;
    /**
     * The ESPN team id, when the face is about a school — #3244.
     *
     * CFBD's team id IS ESPN's, so the mark is derivable with nothing added to
     * the pack. A team face has no headshot, and before this it drew a hole
     * where the mark belongs.
     */
    teamId?: number;
    position?: string;
    profile?: {
        kind: 'player' | 'coach';
        overview: string;
        headshotUrl?: string;
        sourceUrl: string;
        verifiedAt: string;
    };
    rows: TownhallPlayerCardRow[];
    /**
     * What period the numbers cover — "TONIGHT", "SEASON TO DATE".
     *
     * Not decoration: "264 YDS" is not information unless the reader knows
     * whether that is one game or a year.
     */
    period?: string;
    /**
     * #3244 — draw this face as a GRID rather than as rows.
     *
     * `rows` stays populated beside it, so a surface that has not learned the
     * grid still draws something true instead of nothing.
     */
    grid?: TownhallRankingsGridEntry[];
    /** #3244 — a schedule draws its own kind of cell, with its own facts. */
    scheduleGrid?: TownhallScheduleGridEntry[];
    /** #3253 — a national position race: people, themed on their schools. */
    raceGrid?: TownhallRaceGridEntry[];
    /** #3358 — a Saturday slate: fixtures, both marks per cell. */
    slateGrid?: TownhallSlateGridEntry[];
    /** #3260 — coach and production leaders. Team cards only. */
    people?: TownhallTeamPeople;
}

export interface TownhallPlayerCard {
    card?: TownhallPlayerCardFace;
    comparison?: {
        a: TownhallPlayerCardFace;
        b: TownhallPlayerCardFace;
        label?: string;
        /**
         * #3242 — the rows present on BOTH faces, in card order.
         *
         * Sent because it is what the RENDERER aligns on. It was computed
         * server-side and never put on the wire, so the room drew two
         * independent cards whose rows did not line up — USC's four wrapping to
         * two lines beside San José State's three. Two cards side by side is
         * not a side-by-side.
         */
        sharedLabels?: string[];
    };
    caption?: string;
    /** Team colours, already resolved for contrast by the publisher. */
    colors?: { home: string; away: string };
}

/**
 * #3133 — the college-football score bug, as the room receives it.
 *
 * The same data the compositor's scoreboard surface draws from its query
 * string, delivered natively because a room already holds it. Every field is
 * nullable in the way the *game* makes it nullable: before kickoff there is no
 * score, and a bug reading 0-0 then is wrong rather than merely empty.
 *
 * Unlike every other mode on this bus the score bug is AMBIENT — it stays up
 * for three hours while cards, polls and questions come and go over it. That is
 * why it rides its own slot on the client rather than the single exclusive
 * overlay slot: put it there and the next card would take the score off screen,
 * and the next scoreboard tick ten seconds later would take the card off.
 */
export interface TownhallScoreboard {
    gameId: number;
    homeTeam: string;
    awayTeam: string;
    homePoints: number | null;
    awayPoints: number | null;
    /** 1-4 regulation, 5+ overtime. */
    period: number | null;
    clock: string | null;
    /** "2nd & 7 at IND 34" — down, distance and field position as one line. */
    situation: string | null;
    /** Team name, in the scoreboard's own full form ("Indiana Hoosiers"). */
    possession: string | null;
    /** 0-1, home side. Drawn only once the game is actually being played. */
    homeWinProbability: number | null;
    /**
     * The game is over.
     *
     * Derived by the publisher rather than the feed's own `status` string,
     * which is an upstream vocabulary this repo has been bitten trusting. A
     * final bug is a different graphic from a live one: it reads FINAL rather
     * than a quarter, and it drops the clock, the situation, possession and win
     * probability, none of which mean anything once a game has ended.
     */
    final?: boolean;
    /** Team colours, already resolved for contrast by the publisher. */
    colors?: { home: string; away: string };
    /** When the snapshot was fetched, so a stale bug can be recognised. */
    observedAt: number;
}

export interface TownhallStageOverlay {
    visible: boolean;
    mode: TownhallOverlayMode;
    // Player card fields (mode === 'player_card')
    playerCard?: TownhallPlayerCard;
    // #4401 — prediction-market odds fields (mode === 'market_odds')
    marketOdds?: import('./MarketOddsCard').MarketOddsCard;
    // Score bug fields (mode === 'scoreboard')
    scoreboard?: TownhallScoreboard;
    // Poll fields
    pollId?: string;
    pollSnapshot?: TownhallPollSnapshot | null;
    // Sentiment fields (mode === 'sentiment')
    sentimentSnapshot?: AudienceSentimentSnapshot;
    sentimentHistory?: AudienceSentimentSnapshot[];
    // Question fields
    questionId?: string;
    questionText?: string;
    questionAuthor?: string;
    questionSource?: TownhallAudienceEventSource;
    // Search fields
    searchHeadline?: string;
    searchBullets?: string[];
    searchAttribution?: string;
    // Video fields (Slice 2 rendering)
    videoId?: string;
    videoTitle?: string;
    videoThumbnail?: string;
    playlistIndex?: number;
    playlistTotal?: number;
    // Speaker fields
    speakerName?: string;
    speakerRole?: string;
    // Presentation fields (mode === 'presentation')
    presentationCard?: PresentationCard;
    presentationSelection?: import('./PresentationSelection').PresentationSelection;
    // Broadcast brief fields (mode === 'broadcast_brief')
    broadcastBrief?: BroadcastBrief;
    // Web stage fields (mode === 'web_stage' or 'web_stage_preview')
    webStageUrl?: string;
    webStageMetadata?: import('./WebStage').WebStageMetadata;
    webStageCanEmbed?: boolean;
    /** Best available content tier for this URL (server-determined) */
    webStageContentTier?: import('./WebStage').WebStageContentTier;
    // Control fields
    createdBy?: 'host' | 'companion';
    // Legacy fields
    title?: string;
    subtitle?: string;
}

export const TOWNHALL_QUESTION_STATUSES = [
    'pending',
    'pinned',
    'surfaced',
    'dismissed_host',
    'dismissed_companion',
] as const;

export type TownhallQuestionStatus = typeof TOWNHALL_QUESTION_STATUSES[number];

export interface TownhallModerationQuestionEntry extends TownhallChatMessage {
    moderationStatus: TownhallQuestionStatus;
    pinnedAt?: number;
    surfacedAt?: number;
    surfacedByUserId?: string;
    dismissedAt?: number;
    dismissedBy?: 'host' | 'companion';
    dismissedByUserId?: string;
}

// ─── Topic Agenda ───────────────────────────────────────────────────────────

export interface TownhallTopicAgendaSnapshot {
    agenda: TopicAgendaItem[];
    activeTopicIndex: number;
}

// ─── Sentiment ──────────────────────────────────────────────────────────────

export type SentimentVote = 'up' | 'down';

export interface AudienceSentimentSnapshot {
    score: number;
    upCount: number;
    downCount: number;
    totalVoters: number;
    avgIntensity: number;
    windowMs: number;
    sampledAt: number;
}

export interface AudienceSentimentVotePayload {
    vote: SentimentVote;
    intensity: number;
}

export interface AudienceSentimentResponse {
    current: AudienceSentimentSnapshot;
    history: AudienceSentimentSnapshot[];
}

// ─── Audience count ─────────────────────────────────────────────────────────

export interface AudienceCountSnapshot {
    total: number;
    sampledAt: number;
}

export interface AudienceHeartbeat {
    sequenceId: string;
}

export interface AudienceBootstrapPayload {
    roomState: TownhallRoomStateSnapshot;
    activePolls: TownhallPollSnapshot[];
    audienceCapabilities: ParticipationCapabilities;
    sessionPromotion?: PromotionQueueEntry;
    /** Current card overlay state for late-joiner hydration */
    activeCardOverlay?: TownhallStageOverlay | null;
    /** Current video overlay state for late-joiner hydration */
    activeVideoOverlay?: TownhallStageOverlay | null;
    /** Whether the sentiment overlay is currently visible */
    sentimentOverlayVisible?: boolean;
    /** Current topic agenda state for late-joiner hydration */
    topicAgenda?: TownhallTopicAgendaSnapshot | null;
    /** Current presentation overlay state for late-joiner hydration */
    activePresentationOverlay?: TownhallStageOverlay | null;
}

export interface AudienceRoomStateEventPayload {
    snapshot: TownhallRoomStateSnapshot;
    bootstrap?: AudienceBootstrapPayload;
}

export interface AudienceSSEPayloadMap {
    transcript: TranscriptSegment;
    poll_update: TownhallPollSnapshot;
    vote_result: TownhallPollSnapshot;
    room_state: AudienceRoomStateEventPayload;
    reaction_wave: TownhallReactionWave;
    promotion_status: PromotionQueueEntry;
    audience_count: AudienceCountSnapshot;
    chat_message: TownhallChatMessage;
    stage_overlay: TownhallStageOverlay;
    heartbeat: AudienceHeartbeat;
    sentiment_update: AudienceSentimentSnapshot;
    topic_update: TownhallTopicAgendaSnapshot;
    music_now_playing: MusicNowPlaying;
}

export interface AudienceSSEEventData<
    TEvent extends AudienceEventType = AudienceEventType,
> {
    roomId: string;
    timestamp: number;
    payload: AudienceSSEPayloadMap[TEvent];
}

export interface AudienceSSEEvent<
    TEvent extends AudienceEventType = AudienceEventType,
> {
    id: string;
    event: TEvent;
    data: AudienceSSEEventData<TEvent>;
}

export interface AudienceVoteActionPayload {
    pollId: string;
    optionIds: string[];
}

export interface AudienceReactionActionPayload {
    reaction: string;
    /** Optional normalized target used by targeted stage feedback effects. */
    target?: StageFeedbackTarget;
    clientEventId?: string;
}

export interface AudienceRaiseHandActionPayload {
    requestId: string;
    note?: string;
    displayName?: string;
}

export interface AudienceQuestionActionPayload {
    text: string;
}

export interface AudienceChatActionPayload {
    text: string;
}

export type AudienceActionEvent =
    | {
          roomId: string;
          timestamp: number;
          identity: AudienceIdentity;
          action: 'vote';
          payload: AudienceVoteActionPayload;
      }
    | {
          roomId: string;
          timestamp: number;
          identity: AudienceIdentity;
          action: 'poll_response';
          payload: AudienceVoteActionPayload;
      }
    | {
          roomId: string;
          timestamp: number;
          identity: AudienceIdentity;
          action: 'reaction';
          payload: AudienceReactionActionPayload;
      }
    | {
          roomId: string;
          timestamp: number;
          identity: AudienceIdentity;
          action: 'raise_hand';
          payload: AudienceRaiseHandActionPayload;
      }
    | {
          roomId: string;
          timestamp: number;
          identity: AudienceIdentity;
          action: 'question';
          payload: AudienceQuestionActionPayload;
      }
    | {
          roomId: string;
          timestamp: number;
          identity: AudienceIdentity;
          action: 'chat';
          payload: AudienceChatActionPayload;
      }
    | {
          roomId: string;
          timestamp: number;
          identity: AudienceIdentity;
          action: 'sentiment_vote';
          payload: AudienceSentimentVotePayload;
      };

export interface ChatIngestionRule {
    trigger: ChatIngestionRuleTrigger;
    pattern: string;
    action: TownhallAudienceAction;
    extractValue?: boolean;
    cooldownMs?: number;
    platforms?: readonly TownhallAudienceEventSource[];
}

export interface ChatIngestionConfig {
    enabled: boolean;
    platforms: readonly TownhallAudienceEventSource[];
    rules: readonly ChatIngestionRule[];
    includeMessagesInAudienceFeed: boolean;
}
